import { apiUrl } from '../../config/runtime';
import type { ApiErrorBody } from '../../types/api';

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export async function apiFetch<T>(path: `/api/${string}`, init: RequestInit = {}): Promise<T> {
  const response = await fetch(apiUrl(path), {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...jsonHeaders(init.body),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch((): ApiErrorBody => ({}));
    throw new ApiError(response.status, apiErrorMessage(response.status, body));
  }

  return response.json() as Promise<T>;
}

export async function apiRequest(path: `/api/${string}`, init: RequestInit = {}): Promise<void> {
  const response = await fetch(apiUrl(path), {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...jsonHeaders(init.body),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch((): ApiErrorBody => ({}));
    throw new ApiError(response.status, apiErrorMessage(response.status, body));
  }
}

export async function apiText(path: `/api/${string}`): Promise<string> {
  const response = await fetch(apiUrl(path), { credentials: 'same-origin' });
  if (!response.ok) {
    throw new ApiError(response.status, `HTTP ${response.status}`);
  }
  return response.text();
}

function jsonHeaders(body: BodyInit | null | undefined): HeadersInit {
  if (typeof body === 'string') {
    return { 'Content-Type': 'application/json' };
  }
  return {};
}

function apiErrorMessage(status: number, body: ApiErrorBody): string {
  if (status >= 500) {
    return 'Service is temporarily unavailable. Please try again shortly.';
  }
  return body.message || body.error || `HTTP ${status}`;
}
